import Vue from 'vue';
import {API, LOCAL_TIME_DISPLAYING, TIME_ZONE_FROM_SHORT} from '@/const';
import EdjsParser from 'editorjs-parser';
import {cssStringFromObject, teamName} from './helpers';

/**
 * Get corrected path with custom prefix
 * @param {string} url
 * @param {string} prefix
 * @return {string}
 */
const fixPath = (url, prefix) => {
  if (url.startsWith('http')) {
    return url;
  }
  const correctUrl = url.startsWith('/') ? url : `/${url}`;
  return prefix + correctUrl;
};
/**
 * Get source path
 * @param {string} url
 * @return {string|*}
 */
const getCorrectPath = (url) => {
  if (!url) return;
  return fixPath(url, window.document.location.origin);
};

/**
 * Get image path
 * @param {string} url
 * @return {string|*}
 */
Vue.prototype.getImagePath = (url) => {
  if (!url) return;
  return fixPath(url, API.MEDIA_HOST);
};

/**
 *
 * @param {object} data
 * @param {object} config
 * @return {string}
 */
const paragraphHandler = function(data, config) {
  const style = {};
  if (data.fontSize) {
    style['font-size'] = `${data.fontSize}px`;
  }
  return `<p
  class="${config.paragraph.pClass} text-${data.alignment}"
  style='${cssStringFromObject(style)}'
>
    ${Vue.prototype.$t(data.text)}
</p>`;
};

const headerHandler = function({level, text, alignment}) {
  const el = document.createElement('h' + level);
  if (alignment) {
    el.classList.add(`text-${alignment}`);
  }
  el.insertAdjacentHTML('afterbegin', text);

  return el.outerHTML;
};

/**
 * Reformat timeStamp to readable string with custom formats
 * @param {object} data
 * @param {number} data.ts - timeStamp.
 * @param {string} data.timeZone - expected time zone. ['PST' |'America/Los_Angeles']
 * @param {object} data.formatsOptions - object of day format options for each position.
 * @param {Boolean} data.asLocalTime - return time in local timezone or as admin set.
 * @return {Object}
 *
 * @example  input {
 *   ts: 1670251838,
 *   timiZone: 'America/Toronto',
 *   formatsOptions: {
 *     yr: {year: 'numeric'},
 *     month: {month: 'long'},
 *     d: {day: '2-digit'},
 *   },
 *   asLocalTime: true,
 * },
 * return {
 *   yr: '2022',
 *   month: 'December',
 *   d: '05'
 * }
 */
export const getFormattedDate = ({
  ts,
  timeZone,
  formatsOptions,
  asLocalTime = LOCAL_TIME_DISPLAYING,
}) => {
  const date = new Date(ts * 1000);
  const options = {};
  const locale = Vue.prototype.$i18n.locale();
  if (!asLocalTime && timeZone) {
    options.timeZone = TIME_ZONE_FROM_SHORT[timeZone] || timeZone;
  }
  const formattedDate = {};
  Object.entries(formatsOptions).forEach(([key, opt = {}]) => {
    const toLocaleStringFuncCallers = ['hh'];
    let funcName = 'toLocaleDateString';
    if (toLocaleStringFuncCallers.includes(key)) {
      funcName = 'toLocaleString';
    }
    formattedDate[key] = date[funcName]?.(locale, {...options, ...opt});
  });

  const zoneOption = {
    timeZoneName: 'short',
  };
  if (!asLocalTime && timeZone) {
    zoneOption.timeZone = TIME_ZONE_FROM_SHORT[timeZone] || timeZone;
  }
  let zone = date.toLocaleTimeString(locale, {...options, ...zoneOption});
  zone = zone.split(' ');
  zone = zone[zone.length - 1];

  formattedDate.zone = zone;
  return formattedDate;
};
Vue.prototype.trimParticipant = (name) => {
  return teamName(name);
};

const customParsers = {
  /**
   * Override header for processing align
   * @param {object} data;
   * @param {number} data.level;
   * @param {string} data.text;
   * @param {string|undefined} data.alignment;
   * @return {string}
   */
  'header': headerHandler,
  /**
   * Override paragraph for processing align
   * @param {object} data
   * @param {object} config
   * @return {string}
   */
  'paragraph': paragraphHandler,
  /**
   *
   * @param {object} data
   * @param {object} config
   * @return {string}
   */

  'image': function(data, config) {
    const conditions = {
      'img-fullwidth': data.stretched,
      'img-border': data.withBorder,
      'img-bg': data.withBackground,
      'img-center': data.alignment === 'center',
    };
    const imgConditions = Object.entries(conditions)
        .filter(([_, value]) => value)
        .map(([key]) => key)
        .join(' ');
    
    const imgClass = config.image.imgClass || '';
    const linkTarget = data.linkTargetBlank ? '_blank' : '_self';
    const caption = data.caption;
    const linkImage = {
      open: data.wrappedLink ?
        `<a href=${getCorrectPath(data.wrappedLink)} target=${linkTarget}>` :
        '',
      close: data.wrappedLink ? '</a>' : '',
    };

    let imageSrc;
    if (data.url) {
      imageSrc = data.url;
    } else if (config.image.path === 'absolute') {
      imageSrc = data.file.url;
    } else {
      imageSrc = config.image.path.replace(
          /<(.+)>/,
          (match, p1) => data.file[p1],
      );
    }

    if (config.image.use === 'img') {
      return `${linkImage.open}
                <img class="${imgConditions} ${imgClass}" src="${imageSrc}" alt="${caption}">
              ${linkImage.close}`;
    } else if (config.image.use === 'figure') {
      const figureClass = config.image.figureClass || '';
      const figCapClass = config.image.figCapClass || '';

      return `<figure class="${figureClass}">
                ${linkImage.open}
                  <img class="${imgClass} ${imgConditions}" src="${imageSrc}" alt="${caption}">
                ${linkImage.close}
                <figcaption class="${figCapClass}">
                  ${caption}
                </figcaption>
              </figure>`;
    }
  },
};
const parser = new EdjsParser(undefined, customParsers);
/**
 *
 * @param {string} json
 * @return {string}
 */
Vue.prototype.convertEditorJsonToHtml = (json) => {
  // check for empty
  if (!json) {
    return '';
  }

  let html;

  try {
    if (typeof json === 'string') {
      json = JSON.parse(json);
    }
    html = parser.parse(json);
  } catch (e) {
    html = '';
  }

  return html;
};

/**
 * Converts a time string into a Unix timestamp.
 *
 * @param {string} timeString - The time string to convert.
 * @return {number} - The Unix timestamp equivalent of the provided time string.
 */
export function timestampFromTimeString(timeString) {
  return Math.floor(new Date(timeString).getTime() / 1000);
}
